/**
 * Like or unlike a roadmap ("Ideas") comment.
 *
 * Mirrors Flutter RoadmapRepository.likeComment
 *   (lib/data/repository/roadmap_repository.dart:67-94):
 *   1. Optimistically write the like into encrypted SecureStorage (instant UI
 *      feedback) BEFORE the server call.
 *   2. Call the backend to persist the like + notification.
 *   3. Revert SecureStorage to the pre-toggle like on failure (or remove the
 *      key if there was no previous like).
 *
 * Sibling of the content LikeCommentUseCase, swapping the storage key prefix to
 * SECURE_STORAGE_KEYS.ROADMAP_COMMENT_LIKES, the storage shape to
 * RoadmapCommentLikeStorage, and the service to RoadmapCommentService. The
 * ROADMAP_COMMENT_LIKES prefix is the same one syncSecureStorageUseCase
 * .syncRoadmapCommentLikes writes, so the like-state read in the row component
 * sees this optimistic write.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { RoadmapCommentLikeRemapper } from '@/domain/remappers/roadmapCommentLike/roadmapCommentLikeRemapper';
import { RoadmapCommentService } from '@/domain/services/roadmapComment/roadmapCommentService';
import { COMMENTS_CONSTANTS } from '@/domain/constants/commentsConstants';
import type { RoadmapCommentLikeDto } from '@/data/dtos/roadmapCommentLike/roadmapCommentLikeDto';
import type { RoadmapCommentLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { NotificationInfoDto } from '@/data/dtos/info/notificationInfoDto';

export interface LikeRoadmapCommentParams {
  like: RoadmapCommentLikeDto;
  roadmapItemId: string;
  notification: NotificationInfoDto;
  popularity?: number;
}

export class LikeRoadmapCommentUseCase {
  constructor(
    private service = RoadmapCommentService.getInstance(),
    private storage = SecureStorageWebCrypto.getInstance(),
  ) {}

  async execute(params: LikeRoadmapCommentParams): Promise<boolean> {
    const popularity =
      params.popularity ?? COMMENTS_CONSTANTS.DEFAULT_LIKE_POPULARITY_FOR_COMMENT;

    const storageKey =
      SECURE_STORAGE_KEYS.ROADMAP_COMMENT_LIKES + params.like.commentId;
    const previousLike =
      await this.storage.getEncrypted<RoadmapCommentLikeStorage>(storageKey);

    const newLike: RoadmapCommentLikeStorage = RoadmapCommentLikeRemapper.toStorage({
      ...params.like,
      createdDate: previousLike?.createdDate ?? params.like.createdDate,
      updatedDate: Date.now(),
    });
    await this.storage.setEncrypted(storageKey, newLike);

    const success = await this.service.likeComment(
      params.like,
      params.roadmapItemId,
      params.notification,
      popularity,
    );

    if (!success) {
      if (previousLike) {
        await this.storage.setEncrypted(storageKey, previousLike);
      } else {
        await this.storage.remove(storageKey);
      }
    }

    return success;
  }
}

export const likeRoadmapCommentUseCase = new LikeRoadmapCommentUseCase();
